import { createSlice } from "@reduxjs/toolkit";
import { DEFAULT_ACTIVE, getItemById } from "../../statics/NeuesMandatCatalog";

const emptyAddress = () => ({
    company: "",
    line1: "",
    line2: "",
    zip: "",
    city: "",
    country: "",
    vatId: "",
});

const emptyUserDraft = () => ({
    firstName: "",
    lastName: "",
    email: "",
    phoneCode: "+49",
    phone: "",
    userType: "CUSTOMER",
});

export const initialMandateOnboardingState = {
    isSaving: false,
    errorMessage: null,
    successMessage: null,

    name: "",
    phoneCode: "+49",
    phone: "",
    contactUserId: "", // local id of the user chosen as Vertragsinhaber/Ansprechpartner
    invoiceMail: "",

    users: [], // { id, firstName, lastName, email, phoneCode, phone, userType, active:false }
    userFormOpen: false,
    userDraft: emptyUserDraft(),
    userErrors: {},

    address: emptyAddress(),
    altBillingEnabled: false,
    altAddress: emptyAddress(),

    // active integration ids (mirrors the prototype `sel` set)
    sel: [...DEFAULT_ACTIVE],

    errors: {}, // field-level validation flags (e.g. { name:true, invoiceMail:true })
};

let userIdCounter = 0;

const mandateOnboardingSlice = createSlice({
    name: "mandateOnboarding",
    initialState: { ...initialMandateOnboardingState },
    reducers: {
        setField(state, action) {
            const { field, value } = action.payload;
            state[field] = value;
            if (state.errors[field]) {
                delete state.errors[field];
            }
        },
        setAddressField(state, action) {
            const { block, field, value } = action.payload;
            const target = block === "alt" ? state.altAddress : state.address;
            target[field] = value;
        },
        toggleAltBilling(state, action) {
            state.altBillingEnabled = action.payload;
        },

        openUserForm(state) {
            state.userFormOpen = true;
            state.userDraft = emptyUserDraft();
            state.userErrors = {};
        },
        closeUserForm(state) {
            state.userFormOpen = false;
            state.userDraft = emptyUserDraft();
            state.userErrors = {};
        },
        setUserDraftField(state, action) {
            const { field, value } = action.payload;
            state.userDraft[field] = value;
            if (state.userErrors[field]) {
                delete state.userErrors[field];
            }
        },
        setUserErrors(state, action) {
            state.userErrors = action.payload;
        },
        addUser(state, action) {
            userIdCounter += 1;
            state.users.push({ id: `u-${userIdCounter}`, active: false, ...action.payload });
            state.userFormOpen = false;
            state.userDraft = emptyUserDraft();
            state.userErrors = {};
        },
        removeUser(state, action) {
            const removedId = action.payload;
            state.users = state.users.filter((user) => user.id !== removedId);
            if (state.contactUserId === removedId) {
                state.contactUserId = "";
            }
        },

        // Tile activation with the prototype's dependency rules. `linked` payout tiles are handled in
        // the component (toast) and never dispatched here.
        toggleIntegration(state, action) {
            const id = action.payload;
            const item = getItemById(id);
            if (!item || item.linked) {
                return;
            }
            const active = new Set(state.sel);
            if (active.has(id)) {
                active.delete(id);
                if (item.pay) {
                    active.delete(item.pay); // marketplace off → payout off
                }
            } else {
                active.add(id);
                if (item.pay) {
                    active.add(item.pay); // marketplace on → auto-enable payout
                }
                if (item.suggest) {
                    active.add(item.suggest); // shop on → suggest payment (freely deselectable)
                }
            }
            state.sel = Array.from(active);
        },

        setErrors(state, action) {
            state.errors = action.payload;
        },
        clearMessages(state) {
            state.errorMessage = null;
            state.successMessage = null;
        },

        saveMandateRequest(state) {
            state.isSaving = true;
            state.errorMessage = null;
            state.successMessage = null;
        },
        saveMandateSuccess(state) {
            state.isSaving = false;
            state.successMessage = "Mandat wurde angelegt.";
        },
        saveMandateFailure(state, action) {
            state.isSaving = false;
            state.errorMessage = action.payload;
        },
        resetForm() {
            return { ...initialMandateOnboardingState, sel: [...DEFAULT_ACTIVE] };
        },
    },
});

export default mandateOnboardingSlice;
